import { useEffect, useMemo } from 'react';

import { MessageBubble } from './MessageBubble';
import { PlanApprovalCard } from './PlanApprovalCard';
import { ToolGroup, type ToolGroupEntry } from './ToolGroup';

import type { PanelUpdatePayload } from '@shiftagent/shared';

interface Props {
  /** Events from useConversationMessages — mapped by mapEvent(). */
  streamEvents: Record<string, unknown>[];
  /** Assistant message timestamp, applied to each emitted bubble. */
  timestamp?: string | undefined;
  /**
   * Fired when a `platform__panel_update` tool call resolves. The payload is
   * the parsed JSON `result` of the tool_result. The caller is responsible for
   * dispatching into the AgentPanels context.
   */
  onPanelUpdate?: (payload: PanelUpdatePayload) => void;
  /**
   * Called after the user approves a plan via PlanApprovalCard. The parent
   * should send the corresponding [SYSTEM_TRIGGER:plan_approved:<id>] message
   * so the agent continues the playbook.
   */
  onPlanApproved?: (planId: string) => void;
}

const PANEL_UPDATE_TOOL = 'platform__panel_update';
const CREATE_PLAN_TOOL = 'mcp__shiftagent_platform__create_plan';

type TextBlock = { kind: 'text'; text: string };
type ToolGroupBlock = { kind: 'toolgroup'; tools: ToolGroupEntry[] };
type PlanBlock = {
  kind: 'plan';
  planId: string;
  title: string;
  steps: string[];
  dataAccess: Array<{ system: string; data: string; accessLevel: string; expiry: string }>;
};
type Block = TextBlock | ToolGroupBlock | PlanBlock;

interface CollapseResult {
  blocks: Block[];
  panelUpdates: PanelUpdatePayload[];
}

/**
 * Walks the stream event log in order and folds it into a clean list of blocks.
 *
 *   text  text  tool  tool  text  tool  →  [text, toolgroup(2), text, toolgroup(1)]
 *
 * Consecutive tool calls merge into one ToolGroupBlock so they render inside a
 * single bordered trace. Panel-update tools are hoisted out (they render in the
 * side dock). Plan-approval tools get their own PlanBlock in-place.
 */
function collapseEvents(events: Record<string, unknown>[]): CollapseResult {
  const blocks: Block[] = [];
  const panelUpdates: PanelUpdatePayload[] = [];
  const toolById = new Map<string, ToolGroupEntry>();

  let textBuf: string | null = null;
  let currentGroup: ToolGroupBlock | null = null;

  const flushText = () => {
    if (textBuf !== null) {
      blocks.push({ kind: 'text', text: textBuf });
      textBuf = null;
    }
  };

  const flushGroup = () => {
    currentGroup = null;
  };

  for (const evt of events) {
    const type = evt.type as string | undefined;

    if (type === 'content_block_delta') {
      // Text arriving breaks any in-flight tool group, so the next tool call
      // starts a fresh bordered block below the text.
      flushGroup();
      const delta = (evt.delta as string | undefined) ?? '';
      if (!delta) continue;
      textBuf = (textBuf ?? '') + delta;
      continue;
    }

    if (type === 'content_block_start') {
      flushText();
      const id = String(evt.tool_call_id ?? '');
      const name = (evt.tool_name as string | undefined) ?? 'tool';
      const input = evt.tool_input;

      // Panel updates never appear in the conversation stream.
      if (name === PANEL_UPDATE_TOOL) {
        const existing = toolById.get(id);
        if (existing) {
          existing.name = name;
          existing.input = input;
        } else {
          toolById.set(id, { toolCallId: id, name, input, running: true });
        }
        continue;
      }

      // Plan-approval tools are pulled out of the tool group and rendered as
      // a dedicated card (with live status, approve/deny buttons).
      if (name === CREATE_PLAN_TOOL) {
        flushGroup();
        toolById.set(id, { toolCallId: id, name, input, running: true });
        // Placeholder block — we fill in the plan details once the tool result
        // arrives (that's where plan_id and data_access live).
        blocks.push({
          kind: 'plan',
          planId: id,
          title: 'Waiting for plan…',
          steps: [],
          dataAccess: [],
        });
        continue;
      }

      const existing = toolById.get(id);
      if (existing) {
        existing.name = name;
        existing.input = input;
        continue;
      }
      const entry: ToolGroupEntry = {
        toolCallId: id,
        name,
        input,
        output: undefined,
        running: true,
      };
      toolById.set(id, entry);

      if (!currentGroup) {
        currentGroup = { kind: 'toolgroup', tools: [] };
        blocks.push(currentGroup);
      }
      currentGroup.tools.push(entry);
      continue;
    }

    if (type === 'tool_result') {
      const id = String(evt.tool_call_id ?? '');
      const output = (evt.output as string | undefined) ?? '';
      const entry = toolById.get(id);
      if (!entry) continue;
      entry.output = output;
      entry.running = false;

      const result = evt.result as Record<string, unknown> | undefined;

      if (entry.name === PANEL_UPDATE_TOOL) {
        if (
          result &&
          typeof result.tab_id === 'string' &&
          typeof result.title === 'string' &&
          Array.isArray(result.components)
        ) {
          panelUpdates.push(result as unknown as PanelUpdatePayload);
        }
      }

      if (entry.name === CREATE_PLAN_TOOL && result) {
        const planIdx = blocks.findIndex(
          (b) => b.kind === 'plan' && (b as PlanBlock).planId === id,
        );
        if (planIdx >= 0) {
          const resolvedId = typeof result.plan_id === 'string' ? result.plan_id : id;
          const summary =
            typeof result.summary === 'string'
              ? result.summary
              : typeof result.title === 'string'
                ? result.title
                : 'Plan approval required';
          const steps = Array.isArray(result.steps)
            ? (result.steps as unknown[]).map((s) => stepLabel(s))
            : [];
          const dataAccess = Array.isArray(result.data_access)
            ? (result.data_access as Array<Record<string, string>>).map((item) => ({
                system: item.service_name ?? item.system ?? '',
                data: item.reason ?? item.data ?? '',
                accessLevel: item.access_level ?? 'read',
                expiry: item.expiry ?? '1d',
              }))
            : [];
          blocks[planIdx] = {
            kind: 'plan',
            planId: resolvedId,
            title: summary,
            steps,
            dataAccess,
          };
        }
      }
    }
    // All other event types are ignored in this phase.
  }

  flushText();
  return { blocks, panelUpdates };
}

function stepLabel(s: unknown): string {
  if (typeof s === 'string') return s;
  if (s && typeof s === 'object') {
    const obj = s as Record<string, unknown>;
    if (typeof obj.description === 'string') return obj.description;
    if (typeof obj.action === 'string') return obj.action;
    try {
      return JSON.stringify(obj);
    } catch {
      return String(s);
    }
  }
  return String(s);
}

export function TextStreamRenderer({ streamEvents, timestamp, onPanelUpdate, onPlanApproved }: Props) {
  const { blocks, panelUpdates } = useMemo(() => collapseEvents(streamEvents), [streamEvents]);

  // Dispatch panel updates out-of-band. updateTab() is idempotent on tab_id.
  useEffect(() => {
    if (!onPanelUpdate) return;
    for (const p of panelUpdates) onPanelUpdate(p);
  }, [panelUpdates, onPanelUpdate]);

  if (blocks.length === 0) return null;

  // Identify the positional role of each assistant-text bubble so consecutive
  // replies flatten at the seams (matches v1's grouped-assistant column).
  const textBlockIndices: number[] = [];
  blocks.forEach((b, i) => {
    if (b.kind === 'text') textBlockIndices.push(i);
  });
  const positionFor = (i: number): 'solo' | 'first' | 'middle' | 'last' => {
    const pos = textBlockIndices.indexOf(i);
    if (pos < 0) return 'solo';
    if (textBlockIndices.length === 1) return 'solo';
    if (pos === 0) return 'first';
    if (pos === textBlockIndices.length - 1) return 'last';
    return 'middle';
  };

  return (
    <>
      {blocks.map((b, i) => {
        if (b.kind === 'toolgroup') {
          if (b.tools.length === 0) return null;
          return <ToolGroup key={`toolgroup-${i}`} tools={b.tools} />;
        }
        if (b.kind === 'plan') {
          return (
            <PlanApprovalCard
              key={`plan-${b.planId}-${i}`}
              planId={b.planId}
              title={b.title}
              steps={b.steps}
              dataAccess={b.dataAccess}
              onApproved={onPlanApproved}
            />
          );
        }
        const position = positionFor(i);
        // Only show the agent label on the first/solo bubble of a run.
        const showLabel = position === 'first' || position === 'solo';
        // Only show timestamp on the last/solo bubble of a run.
        const bubbleTimestamp = position === 'last' || position === 'solo' ? timestamp : undefined;
        return (
          <MessageBubble
            key={`text-${i}`}
            role="assistant"
            content={b.text}
            groupPosition={position}
            showAgentLabel={showLabel}
            timestamp={bubbleTimestamp}
          />
        );
      })}
    </>
  );
}
